import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Minus, Plus, ShoppingBag, ArrowLeft, Star } from "lucide-react";
import { toast } from "sonner";
import CantalBadge from "../components/CantalBadge";
import ProductGallery from "../components/ProductGallery";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();
  const { user } = useAuth();
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");

  const loadReviews = () => api.get(`/products/${id}/reviews`).then((r) => setReviews(r.data));

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setProduct(r.data));
    loadReviews();
  }, [id]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) return toast.error("Connectez-vous pour laisser un avis");
    try {
      await api.post(`/products/${id}/reviews`, { rating, comment });
      setComment(""); setRating(5);
      toast.success("Merci pour votre avis !");
      loadReviews();
      api.get(`/products/${id}`).then((r) => setProduct(r.data));
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (!product) return <div className="max-w-7xl mx-auto px-6 py-20">Chargement...</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-testid="product-detail-page">
      <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-[#6B655C] hover:text-terracotta mb-8">
        <ArrowLeft size={16} /> Retour à la boutique
      </Link>

      <div className="grid lg:grid-cols-2 gap-12">
        {/* GALLERY */}
        <ProductGallery images={product.images || []} productName={product.name} />

        <div>
          {product.category && (
            <p className="text-xs uppercase tracking-[0.22em] text-[#6B655C] mb-3" data-testid="product-category">{product.category}</p>
          )}
          <h1 className="font-serif-display text-4xl sm:text-5xl leading-tight mb-4" data-testid="product-name">{product.name}</h1>
          <p className="font-accent text-2xl text-mustard -rotate-2 mb-4">{product.materials}</p>
          <p className="text-3xl text-terracotta font-medium mb-4" data-testid="product-price">{product.price.toFixed(2)} €</p>
          <div className="mb-8"><CantalBadge size="lg" /></div>

          <p className="text-[#1F1C18]/80 leading-relaxed mb-8 whitespace-pre-line">{product.description}</p>

          <div className="bg-mustard/10 border border-mustard/30 rounded-2xl p-4 mb-8">
            <p className="font-accent text-xl text-mustard -rotate-1">Promo Trio ♡</p>
            <p className="text-sm text-[#1F1C18]/80">3 articles achetés = <strong>-10%</strong> sur toute la commande (auto au panier)</p>
          </div>

          <div className="flex items-center gap-3 mb-8">
            <p className="text-sm uppercase tracking-[0.18em] text-[#6B655C]">Quantité</p>
            <div className={`flex items-center border border-[#E5E1D8] rounded-full ${product.stock === 0 ? "opacity-40 pointer-events-none" : ""}`}>
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:text-terracotta" data-testid="qty-minus"><Minus size={16} /></button>
              <span className="px-4 min-w-[2rem] text-center" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="p-3 hover:text-terracotta" data-testid="qty-plus"><Plus size={16} /></button>
            </div>
            <span className={`text-xs ml-2 ${product.stock === 0 ? "text-terracotta font-medium uppercase tracking-wider" : "text-[#6B655C]"}`} data-testid="stock-indicator">
              {product.stock === 0 ? "Rupture de stock" : `${product.stock} en stock`}
            </span>
          </div>

          <button
            onClick={() => { addItem(product, qty); toast.success("Ajouté au panier"); }}
            disabled={product.stock === 0}
            className={`w-full justify-center text-base ${product.stock === 0 ? "inline-flex items-center gap-2 px-8 py-3 rounded-full bg-[#E5E1D8] text-[#6B655C] cursor-not-allowed font-medium" : "btn-primary"}`}
            data-testid="add-to-cart-btn"
          >
            <ShoppingBag size={18} /> {product.stock === 0 ? "Produit épuisé" : "Ajouter au panier"}
          </button>

          <div className="mt-10 pt-8 border-t border-[#E5E1D8] text-sm text-[#6B655C] space-y-2">
            <p>✦ Pièce unique, crochetée à la main</p>
            <p>✦ Livraison soignée sous 3-5 jours</p>
            <p>✦ Retours acceptés sous 14 jours</p>
          </div>
        </div>
      </div>

      {/* REVIEWS */}
      <section className="mt-24 max-w-3xl mx-auto" data-testid="reviews-section">
        <p className="font-accent text-3xl text-terracotta -rotate-2 mb-2 text-center">Avis</p>
        <h2 className="font-serif-display text-3xl sm:text-4xl text-center mb-10">
          {product.rating > 0 ? `${product.rating.toFixed(1)} ★ — ${product.reviews_count} avis` : "Soyez le premier à laisser un avis"}
        </h2>

        {user && (
          <form onSubmit={submitReview} className="bg-white rounded-3xl p-6 border border-[#E5E1D8] mb-10">
            <p className="text-sm uppercase tracking-[0.18em] text-[#6B655C] mb-3">Votre note</p>
            <div className="flex gap-2 mb-4">
              {[1, 2, 3, 4, 5].map((n) => (
                <button type="button" key={n} onClick={() => setRating(n)} data-testid={`star-${n}`}>
                  <Star size={24} className={n <= rating ? "fill-mustard text-mustard" : "text-[#E5E1D8]"} />
                </button>
              ))}
            </div>
            <textarea
              value={comment} onChange={(e) => setComment(e.target.value)} required rows={3}
              placeholder="Partagez votre expérience..."
              className="w-full p-3 rounded-2xl border border-[#E5E1D8] focus:outline-none focus:border-terracotta text-sm"
              data-testid="review-comment"
            />
            <button type="submit" className="btn-primary mt-4" data-testid="submit-review-btn">Publier</button>
          </form>
        )}

        <div className="space-y-6">
          {reviews.map((r) => (
            <div key={r.id} className="bg-white rounded-3xl p-6 border border-[#E5E1D8]" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="font-medium">{r.user_name}</p>
                <div className="flex gap-1">
                  {Array.from({ length: r.rating }).map((_, i) => <Star key={i} size={14} className="fill-mustard text-mustard" />)}
                </div>
              </div>
              <p className="text-sm text-[#1F1C18]/80">{r.comment}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
